import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { Size } from "../helpers/helpers.js";

export interface AutoScrollResumeButtonProps {
  className?: string;
  direction?: "down" | "up";
  onResume: () => void;
}

/** Shared recovery action for transcript views that follow active playback. */
export const AutoScrollResumeButton: FC<AutoScrollResumeButtonProps> = ({
  className,
  direction = "down",
  onResume,
}) => {
  const DirectionIcon = direction === "up" ? IconArrowUp : IconArrowDown;

  return (
    <Button
      className={twMerge("shrink-0", className)}
      icon={<DirectionIcon className="size-4" />}
      size={Size.MEDIUM}
      variant={ButtonVariant.Secondary}
      onClick={onResume}
    >
      Back to current
    </Button>
  );
};

export interface FloatingAutoScrollResumeButtonProps extends AutoScrollResumeButtonProps {
  isVisible: boolean;
}

/**
 * Floating variant that sticks to the target's edge of the nearest scrolling
 * ancestor when rendered in flow inside the scrollable content.
 */
export const FloatingAutoScrollResumeButton: FC<
  FloatingAutoScrollResumeButtonProps
> = (props) => {
  const { className, direction = "down", isVisible, onResume } = props;
  return (
    <div
      inert={!isVisible}
      aria-hidden={!isVisible}
      className={twMerge(
        "pointer-events-none sticky z-20 flex h-0 shrink-0 justify-center",
        direction === "up" ? "top-4 order-first" : "bottom-4",
      )}
    >
      {/* Reserve the 2px animation travel so hidden buttons cannot add overflow. */}
      <div
        className={twMerge(
          "h-fit py-[2px]",
          direction === "down" && "-translate-y-full",
        )}
      >
        <div
          className={twMerge(
            "transition-[opacity,scale,translate,display] transition-discrete motion-reduce:transition-none starting:scale-[0.96] starting:opacity-0",
            direction === "up"
              ? "origin-top starting:-translate-y-[2px]"
              : "origin-bottom starting:translate-y-[2px]",
            isVisible
              ? "block translate-y-0 scale-100 opacity-100 duration-150 ease-out"
              : "hidden scale-[0.96] opacity-0 duration-[120ms] ease-in",
            !isVisible &&
              (direction === "up" ? "-translate-y-[2px]" : "translate-y-[2px]"),
          )}
        >
          <AutoScrollResumeButton
            className={twMerge(
              isVisible && "pointer-events-auto",
              "shadow-md",
              className,
            )}
            direction={direction}
            onResume={onResume}
          />
        </div>
      </div>
    </div>
  );
};
